import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';

/**
 * Sube un archivo de audio a Firebase Storage
 * @param audioBlob Blob del archivo de audio
 * @param path Ruta en Firebase Storage
 * @param onProgress Función de callback para el progreso de la subida
 * @returns URL de descarga del archivo
 */
export const uploadAudio = async (
  audioBlob: Blob,
  path: string,
  onProgress?: (progress: number) => void
): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      // Validar que el blob tenga contenido
      if (!audioBlob || audioBlob.size === 0) {
        console.error('Error: El blob de audio está vacío');
        reject(new Error('El audio grabado está vacío. Por favor, intenta grabar de nuevo.'));
        return;
      }

      console.log('Iniciando subida de audio:', {
        size: audioBlob.size,
        type: audioBlob.type,
        path: path
      });

      // Crear una referencia al archivo en Firebase Storage
      const audioRef = ref(storage, path);

      // Crear una tarea de subida
      const uploadTask = uploadBytesResumable(audioRef, audioBlob);

      // Registrar eventos para la tarea de subida
      uploadTask.on(
        'state_changed',
        // Progreso
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Progreso de subida: ${progress.toFixed(2)}%`);
          if (onProgress) {
            onProgress(progress);
          }
        },
        // Error
        (error) => {
          console.error('Error al subir audio:', error);
          // Proporcionar mensajes de error más específicos
          let errorMessage = 'Error al subir el audio.';
          if (error.code === 'storage/unauthorized') {
            errorMessage = 'No tienes permisos para subir archivos. Verifica que hayas iniciado sesión.';
          } else if (error.code === 'storage/canceled') {
            errorMessage = 'La subida fue cancelada.';
          } else if (error.code === 'storage/unknown') {
            errorMessage = 'Error desconocido al subir el audio. Inténtalo de nuevo.';
          }
          reject(new Error(errorMessage));
        },
        // Completado
        async () => {
          try {
            // Obtener la URL de descarga
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('Audio subido con éxito. URL:', downloadURL);
            resolve(downloadURL);
          } catch (error) {
            console.error('Error al obtener URL de descarga:', error);
            reject(new Error('Error al obtener la URL del audio subido.'));
          }
        }
      );
    } catch (error) {
      console.error('Error al iniciar la subida del audio:', error);
      reject(new Error('Error al iniciar la subida del audio. Verifica tu conexión a internet.'));
    }
  });
};

/**
 * Genera un nombre de archivo único para un audio
 * @param userId ID del usuario
 * @returns Nombre de archivo único con ruta completa
 */
export const generateAudioFileName = (userId: string): string => {
  const timestamp = new Date().getTime();
  const randomString = Math.random().toString(36).substring(2, 10);
  // Incluir una ruta de carpeta para organizar los audios
  return `audio_messages/${userId}/audio_${timestamp}_${randomString}.webm`;
};

/**
 * Obtiene la duración de un archivo de audio
 * @param audioBlob Blob del archivo de audio
 * @returns Duración en segundos
 */
export const getAudioDuration = async (audioBlob: Blob): Promise<number> => {
  return new Promise((resolve, _reject) => {
    try {
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      // Manejar el evento de carga de metadatos
      audio.addEventListener('loadedmetadata', () => {
        // Verificar si la duración es válida
        if (isNaN(audio.duration) || audio.duration === Infinity) {
          console.warn('No se pudo determinar la duración del audio, usando valor predeterminado');
          // Usar un valor predeterminado si no se puede determinar la duración
          URL.revokeObjectURL(audioUrl);
          resolve(30); // Usar 30 segundos como valor predeterminado en lugar de 0
          return;
        }

        // Liberar la URL del objeto
        URL.revokeObjectURL(audioUrl);
        console.log('Duración del audio:', audio.duration);
        resolve(audio.duration);
      });

      // Manejar errores
      audio.addEventListener('error', (error) => {
        console.error('Error al cargar el audio para obtener duración:', error);
        URL.revokeObjectURL(audioUrl);
        // En caso de error, devolver un valor predeterminado en lugar de rechazar
        resolve(0);
      });

      // Establecer un tiempo de espera por si acaso
      setTimeout(() => {
        if (audio.duration === 0 || isNaN(audio.duration)) {
          console.warn('Tiempo de espera agotado al obtener la duración, usando valor predeterminado');
          URL.revokeObjectURL(audioUrl);
          resolve(0);
        }
      }, 3000);

      // Cargar el audio
      audio.load();
    } catch (error) {
      console.error('Error inesperado al obtener la duración del audio:', error);
      resolve(0);
    }
  });
};

/**
 * Formatea la duración de un audio en formato MM:SS
 * @param seconds Duración en segundos
 * @returns Duración formateada
 */
export const formatAudioDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};
